// KB search result card with enhanced metrics - 95 lines ✅
// @requirement SPEC-KB-MGMT-001/FR-05: Track success metrics
// @requirement SPEC-KB-SEARCH-001/FR-05: Display match explanation

import { Link } from 'react-router-dom'
import { TrendingUp, Calendar, ExternalLink } from 'lucide-react'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { VersionBadge } from './VersionBadge'
import { ConfidenceScore } from './ConfidenceScore'
import { ResolutionStatusBadge } from './ResolutionStatusBadge'
import { formatDate } from '@/lib/utils'
import type { SearchResult } from '@/types/knowledge'

interface KBResultCardProps {
  result: SearchResult
  query?: string
  showMatchExplanation?: boolean
  onClick?: () => void
}

export function KBResultCard({
  result,
  showMatchExplanation = false,
  onClick,
}: KBResultCardProps): JSX.Element {
  const { issue, similarity, matchExplanation, resolutionMethods, compositeScore } = result

  const getResolutionStatus = (): 'validated' | 'pending' | 'deprecated' | 'draft' => {
    if (issue.isValidated) return 'validated'
    if (issue.isArchived) return 'deprecated'
    return 'pending'
  }

  return (
    <Link to={`/knowledge/${issue.id}`} onClick={onClick}>
      <Card className="transition-colors hover:bg-kaycoo-teal-pale hover:border-kaycoo-teal">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <CardTitle className="text-lg font-medium text-kaycoo-charcoal mb-2 group-hover:text-kaycoo-teal-dark">
                {issue.title}
              </CardTitle>
              <div className="flex flex-wrap gap-2">
                <ResolutionStatusBadge status={getResolutionStatus()} />
                {issue.d365Versions.map((version) => (
                  <VersionBadge key={version} version={version} />
                ))}
                {issue.d365Modules.map((module) => (
                  <Badge key={module} variant="outline" className="border-kaycoo-blue text-kaycoo-blue">
                    {module}
                  </Badge>
                ))}
              </div>
            </div>
            <ConfidenceScore score={similarity} showLabel={false} />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-kaycoo-charcoal-2 line-clamp-2">
            {issue.description}
          </p>

          {/* Success metrics inline */}
          <div className="flex items-center gap-4 text-sm">
            {issue.successRate != null && issue.successRate > 0 && (
              <div className="flex items-center gap-1 text-kaycoo-teal-dark">
                <TrendingUp className="h-4 w-4" />
                <span className="font-medium">{Math.round(issue.successRate * 100)}% success</span>
              </div>
            )}
            {resolutionMethods.length > 0 && (
              <span className="text-kaycoo-charcoal-2">
                {resolutionMethods.length} solution{resolutionMethods.length !== 1 ? 's' : ''}
              </span>
            )}
            {issue.usageCount > 0 && (
              <span className="text-kaycoo-charcoal-2">
                {issue.usageCount} use{issue.usageCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {showMatchExplanation && matchExplanation && (
            <div className="rounded-md bg-kaycoo-blue-pale border border-kaycoo-blue-light p-3 text-sm">
              <span className="font-medium text-kaycoo-blue-dark">Why this matches: </span>
              <span className="text-kaycoo-charcoal">{matchExplanation}</span>
              <div className="mt-2 text-xs text-kaycoo-charcoal-2">
                Composite score: {Math.round(compositeScore * 100)}%
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex items-center justify-between text-sm text-kaycoo-charcoal-2">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>Updated {formatDate(issue.updatedAt)}</span>
          </div>
          <ExternalLink className="h-4 w-4 text-kaycoo-teal-dark opacity-0 group-hover:opacity-100 transition-opacity" />
        </CardFooter>
      </Card>
    </Link>
  )
}
